/**
 * Chart Utility Functions
 * Provides download functionality for ECharts instances
 * Used across all chart components for consistent download behavior
 */

import * as echarts from 'echarts';
import { log } from '../services/LoggingService';
import { ChartSizingUtils } from './ChartSizingUtils';
import { FilenameUtils } from './filenameUtils';

export type AspectRatio = 'standard' | 'square' | 'story';
export type Platform = 'optimized' | 'instagram' | 'story';

/**
 * Chart dimensions for different aspect ratios and contexts
 */
export const getChartDimensions = (aspectRatio: AspectRatio, context: string = 'standard') => {
  const baseWidth = 800;

  // Email-optimized dimensions for better newsletter rendering - Much larger for visibility
  if (context === 'email') {
    return {
      width: 1600,  // Much larger width for better email visibility
      height: 900   // Taller height for data labels and mobile readability
    };
  }

  // Mobile email optimization - much larger for readability
  if (context === 'mobile-email') {
    return {
      width: 1800,  // Even larger width for mobile email clients
      height: 1000  // Taller height for maximum mobile readability
    };
  }

  switch (aspectRatio) {
    case 'square':
      return { width: 1200, height: 1200 }; // Larger 1:1 for Instagram/LinkedIn
    case 'story':
      return { width: 1000, height: 1800 }; // Larger 9:16 for Stories
    default:
      return {
        width: context === 'social' ? 1800 : baseWidth, // Much larger for social media
        height: context === 'mobile' ? 1000 : context === 'social' ? 900 : 600 // Larger heights for better readability
      };
  }
};

/**
 * Universal chart download function
 * Handles all download contexts with consistent naming and optimal sizing
 */
export const downloadChart = (
  chartInstance: echarts.ECharts,
  filename: string,
  context: 'blog' | 'email' | 'social' | 'presentation' = 'blog'
) => {
  try {
    // Check if chart instance is valid and not disposed
    if (!chartInstance || chartInstance.isDisposed()) {
      console.warn('Chart instance is not available or has been disposed. Skipping download.');
      return;
    }

    // Export with current layout and dimensions - no resizing to maintain exact spacing
    const imageData = chartInstance.getDataURL({
      type: 'png',
      pixelRatio: context === 'social' ? 4 : context === 'email' ? 3 : 2.5, // High DPI for crisp exports
      backgroundColor: '#ffffff'
    });

    // Create download link
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    log.debug(`Chart downloaded as: ${filename} (${context})`, "Debug");

  } catch (error) {
    console.error('Failed to download chart:', error);
    // Silent fail - don't show alert as this may be called during chart transitions
  }
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use downloadChart instead
 */
export const downloadChartAsPNG = (chartInstance: echarts.ECharts, filename: string) => {
  downloadChart(chartInstance, filename, 'blog');
};

/**
 * Legacy functions for backward compatibility
 * @deprecated Use downloadChart instead
 */
export const downloadForSocialMedia = (
  chartInstance: echarts.ECharts,
  chartType: string,
  aspectRatio: AspectRatio,
  platform: Platform,
  customFilename?: string
) => {
  const context = platform === 'instagram' ? 'social' : 
                 platform === 'story' ? 'social' : 'social';
  const filename = customFilename || FilenameUtils.generateChartFilename(
    chartType,
    '12w',
    context
  );
  downloadChart(chartInstance, filename, 'social');
};

export const downloadForEmail = (
  chartInstance: echarts.ECharts,
  chartType: string,
  customFilename?: string
) => {
  const filename = customFilename || FilenameUtils.generateChartFilename(
    chartType,
    '12w',
    'email'
  );
  downloadChart(chartInstance, filename, 'email');
};

/**
 * Standardized grid configurations for consistent spacing
 * Used across all chart types to ensure uniformity
 */
export const CHART_GRID = {
  email: {
    left: '6%',
    right: '4%',
    bottom: '16%', // Increased for axis labels and CTA
    top: '15%',
    containLabel: true
  },
  desktop: {
    left: '10%',
    right: '8%',
    bottom: '18%',
    top: '25%',
    containLabel: true
  }
};

/**
 * Common chart styling constants
 * Reused across all chart components for consistency
 *
 * STANDARDIZED VALUES (2025-10-23):
 * - X-axis: 16px, weight 600
 * - Y-axis: 16px, weight 600
 * - Legend: 16px responsive, weight 400, CENTER ALIGNED (all charts)
 * - Legend itemGap: 15px (moderate spacing)
 * - Grid bottom: 20% (allows legend 10% + CTA 7% + margin 3%)
 * - CTA bottom: 3%, separator: 7%
 */
export const CHART_STYLES = {
  title: {
    fontSize: 24, // Moderate increase for better readability without overflow
    fontWeight: 'bold' as const,
    color: '#1f2937',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
  },
  subtitle: {
    fontSize: 18, // Moderate increase that fits within layout
    fontWeight: '500' as const,
    color: '#6b7280',
    lineHeight: 22,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
  },
  xAxis: {
    fontSize: 16,        // Standardized at 16px across all chart types
    fontWeight: 600,     // Bold for better readability
    color: '#6b7280'
  },
  yAxis: {
    fontSize: 16,        // Standardized at 16px across all chart types
    fontWeight: 600,     // Bold for better readability
    color: '#6b7280'
  },
  legend: {
    fontSize: 16,        // Responsive: 12px mobile, 14px tablet, 16px desktop
    fontWeight: 400,     // Normal weight for legends
    color: '#374151',
    itemGap: 15,         // Moderate spacing between legend items
    alignment: 'center' as const, // Center alignment for all charts (standardized)
    bottom: '10%'        // 10% from bottom (standardized)
  },
  tooltip: {
    fontSize: 16         // Moderate increase for tooltip text
  },
  cta: {
    fontSize: 14,        // Moderate increase for CTA text (responsive: 14-20px)
    color: '#6b7280',
    bottom: '3%',        // Standardized at 3% from bottom
    separatorBottom: '7%' // Separator at 7% from bottom
  },
  dataLabel: {
    fontSize: 13,        // Moderate increase for data labels
    color: '#374151'
  },
  grid: {
    left: '1%',
    right: '1%',
    top: '15%',          // Standard across all chart types
    bottom: '20%',       // Room for legend (10%) + CTA (7%) + margin (3%)
    containLabel: true
  },
  // Legacy support - use xAxis/yAxis instead
  axis: {
    fontSize: 16,
    fontWeight: 600,
    color: '#6b7280'
  }
} as const;

/**
 * Unified monetary value formatter
 * Ensures consistent formatting across all visualizations
 */
export const formatMonetaryValue = (value: number, context: 'axis' | 'tooltip' | 'label' = 'axis'): string => {
  // Handle zero and invalid values
  if (!value || value === 0) {
    return context === 'label' ? '' : '$0.0';
  }

  const absValue = Math.abs(value);

  // Values in billions (>= 1B)
  if (absValue >= 1000000000) {
    const billions = value / 1000000000;
    return `$${billions.toFixed(1)}B`;
  }

  // Values in millions (>= 1M)
  if (absValue >= 1000000) {
    const millions = value / 1000000;
    return `$${millions.toFixed(1)}M`;
  }

  // Values in thousands (>= 1K)
  if (absValue >= 1000) {
    const thousands = value / 1000;
    return `$${thousands.toFixed(1)}K`;
  }

  return `$${value.toFixed(1)}`;
};